import { BUTTON_LABELS } from '@/constants';
import type { ICardAction, IScorecardActions } from '@/types/scoring-actions';

// ================================
// Types
// ================================

/**
 * Input contract for the next-roleplay card action.
 *
 * Keeps the helper scoped to the minimum state it needs: lock state and
 * the already-resolved click handler from the feature layer.
 */
type BuildNextRoleplayActionsParams = {
  isNextSimulationLocked?: boolean;
  onStartNextRoleplayClick: ICardAction['onClick'];
};

/**
 * Input contract for scorecard action assembly.
 *
 * Presentation-specific class names stay caller-controlled so the policy can
 * centralize action shape without hard-coding feature-only styling decisions.
 */
type BuildRoleplayScorecardActionsParams = {
  actionBaseClassName?: string;
  actionHiddenClassName?: string;
  isRetakeRoleplayVisible?: boolean;
  onRetakeRoleplayClick: () => void;
  onShareWithManagerClick?: () => void;
};

// ================================
// Internal Helpers
// ================================

/**
 * Resolves action className while allowing feature-level styling concerns
 * to be passed in as parameters.
 *
 * Behavior:
 * - always includes the base class when provided
 * - appends the hidden class only when the action should be suppressed
 */
const buildActionVisibilityClassName = ({
  actionBaseClassName = 'no-print',
  actionHiddenClassName = 'hidden',
  isVisible = true,
}: {
  actionBaseClassName?: string;
  actionHiddenClassName?: string;
  isVisible?: boolean;
}): string => {
  return [actionBaseClassName, isVisible ? '' : actionHiddenClassName].filter(Boolean).join(' ');
};

// ================================
// Public Domain Policy
// ================================

/**
 * Builds the single next-roleplay card action used by simulation score views.
 *
 * Behavior parity with the prior hook implementation:
 * - label remains "Get started"
 * - variant remains primary
 * - disabled state follows locked next-simulation status
 */
export const buildNextRoleplayActions = ({
  isNextSimulationLocked,
  onStartNextRoleplayClick,
}: BuildNextRoleplayActionsParams): ICardAction[] => {
  return [
    {
      disabled: isNextSimulationLocked,
      label: BUTTON_LABELS.GET_STARTED,
      onClick: onStartNextRoleplayClick,
      size: 'small',
      type: 'button',
      variant: 'primary',
    },
  ];
};

/**
 * Builds scorecard primary/secondary action configuration with visibility
 * controls parameterized by caller intent.
 *
 * Notes:
 * - both actions share the same visibility class policy
 * - share action keeps a no-op fallback until a real handler is supplied
 * - labels stay centralized here to reduce duplication across score surfaces
 */
export const buildRoleplayScorecardActions = ({
  actionBaseClassName,
  actionHiddenClassName,
  isRetakeRoleplayVisible,
  onRetakeRoleplayClick,
  onShareWithManagerClick = () => undefined,
}: BuildRoleplayScorecardActionsParams): IScorecardActions => {
  const className = buildActionVisibilityClassName({
    actionBaseClassName,
    actionHiddenClassName,
    isVisible: isRetakeRoleplayVisible,
  });

  return {
    primary: {
      className,
      label: BUTTON_LABELS.TRY_AGAIN,
      onClick: onRetakeRoleplayClick,
    },
    secondary: {
      className,
      label: BUTTON_LABELS.SHARE_WITH_MANAGER,
      onClick: onShareWithManagerClick,
    },
  };
};
